import { hasFailed } from "@miragon/react-utils-api";
import { Delete, PlayArrow } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import QATableButton from "components/Design/Table/Controls/QATableButton";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import QAButton from "../../components/Design/Controls/QAButton";
import QAButtonRow from "../../components/Design/Layout/QAButtonRow";
import QATableShortcut from "../../components/Design/Table/Controls/QATableShortcut";
import QATableButtonRow from "../../components/Design/Table/Layout/QATableButtonRow";
import QATable, { ColumnDefinition } from "../../components/Design/Table/QATable";
import ConfirmationDialog from "../../components/Hooks/Dialog/ConfirmationDialog";
import useDialog from "../../components/Hooks/Dialog/useDialog";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import { Routing } from "../../data/Constants/Routing";
import { useAppDispatch } from "../../state/Store";
import { ControllerAPI, ScenarioRO } from "../../api/api";
import { updateScenariosManually } from "../../state/ScenariosState";
import { updateExecutionsManually } from "../../state/ExecutionsState";
import CreateExecutionDialog from "./Dialog/CreateExecutionDialog";

interface Props {
    scenarios: ScenarioRO[];
}

const ScenarioTable: React.FC<Props> = props => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const dialog = useDialog();
    const dispatch = useAppDispatch();

    const { scenarios } = props;

    const onDelete = useCallback(async (scenario: ScenarioRO) => {
        const confirmation = await dialog.open(ConfirmationDialog, {
            title: "Szenario löschen",
            question: `Wollen Sie das Szenario "${scenario.name}" wirklich löschen? Dies kann nicht rückgängig gemacht werden.`,
            acceptButtonTitle: "Löschen"
        });

        if (!confirmation.accepted) {
            return;
        }

        const response = await ControllerAPI.deleteScenario(scenario.id);
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateScenariosManually({
                delete: [scenario]
            }));
            snackbar.onSuccess("Szenario gelöscht.");
        }
    }, [dialog, snackbar, dispatch]);

    const onStart = useCallback(async (scenario: ScenarioRO) => {
        const confirmation = await dialog.open(CreateExecutionDialog, { scenario });

        if (!confirmation.accepted) {
            return;
        }

        const response = await ControllerAPI.createExecution(confirmation.result);
        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateExecutionsManually({
                update: [response.result]
            }));
            snackbar.onSuccess("Ausführung erstellt.");
            navigate(Routing.Administration.Execution.Edit.Navigate(response.result.id));
        }
    }, [dialog, snackbar, dispatch, navigate]);

    const columns: ColumnDefinition<ScenarioRO>[] = useMemo(() => [
        {
            id: "group",
            title: "Gruppe",
            width: "200px",
            sortable: true,
            getValue: item => item.group,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Scenario.Edit.Navigate(item.id)}>
                        {item.group}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "name",
            title: "Name",
            width: "200px",
            sortable: true,
            getValue: item => item.name,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Scenario.Edit.Navigate(item.id)}>
                        {item.name}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "roles",
            title: "Rollen",
            width: "auto",
            sortable: true,
            getValue: item => item.roles.length,
            renderCell: item => {
                return (
                    <QATableShortcut target={Routing.Administration.Scenario.Edit.Navigate(item.id)}>
                        {item.roles.map((role, index) => (
                            <React.Fragment key={role.id}>
                                <span><b>{role.instances.length}x</b> {role.name}</span>
                                {index !== item.roles.length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </QATableShortcut>
                );
            }
        },
        {
            id: "actions",
            width: "100px",
            padding: 0,
            getValue: () => undefined,
            renderCell: item => (
                <QATableButtonRow>
                    <QATableButton
                        icon={PlayArrow}
                        onClicked={() => onStart(item)} />
                    <QATableButton
                        icon={Edit}
                        onClicked={() => navigate(Routing.Administration.Scenario.Edit.Navigate(item.id))} />
                    <QATableButton
                        icon={Delete}
                        onClicked={() => onDelete(item)} />
                </QATableButtonRow>
            )
        }
    ], [navigate, onDelete, onStart]);

    return (
        <>

            <QAButtonRow alignRight marginBottom>
                <QAButton
                    light
                    size="small"
                    title="Erstellen"
                    onClicked={() => navigate(Routing.Administration.Scenario.Create.Navigate())} />
            </QAButtonRow>

            <QATable
                defaultSortByColumn="name"
                items={scenarios}
                defaultPageSize={25}
                getId={item => item.id}
                fallbackText="Keine Szenarien gefunden."
                columns={columns} />

        </>

    );
};

export default ScenarioTable;
